import { useState, useEffect, useRef } from 'react';

function parseBRL(str) {
  const s = String(str ?? '').trim();
  if (!s) return 0;
  // Plain numeric string (positive or negative) e.g. "-1234.56", "1234.56", "1234"
  if (/^-?[\d]+(\.\d+)?$/.test(s)) return parseFloat(s) || 0;
  // Brazilian formatted: "R$ 1.234,56" or "-R$ 1.234,56" or "R$ -1.234,56"
  const negative = s.replace(/[^0-9\-]/g, '').startsWith('-');
  const cleaned = s
    .replace(/R\$\s*/g, '')   // remove R$
    .replace(/[^\d,.\-]/g, '') // keep only digits, comma, dot, minus
    .replace(/\./g, '')        // strip thousands dots
    .replace(',', '.')         // decimal comma → dot
    .replace(/[^\d.\-]/g, '') // strip anything else
    .trim();
  const val = parseFloat(cleaned);
  if (isNaN(val)) return 0;
  // Ensure sign is preserved when minus was present but parseFloat lost it
  return negative && val > 0 ? -val : val;
}

function formatBRL(val) {
  const num = parseBRL(val);
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Keys allowed during typing (non-printable / control keys)
const ALLOWED_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
  'Home', 'End',
]);

/**
 * Controlled currency input that displays values as "R$ 1.234,56" or "-R$ 1.234,56".
 * Emits onChange with a synthetic event { target: { name, value } }
 * where value is a numeric string like "1234.56" or "-1234.56".
 * Only digits, minus sign, comma and dot are accepted — letters are blocked.
 */
function CurrencyInput({ name, value, onChange, className = 'form-control', required, disabled }) {
  const isFocused = useRef(false);
  const [display, setDisplay] = useState(formatBRL(value));

  useEffect(() => {
    if (!isFocused.current) {
      setDisplay(formatBRL(value));
    }
  }, [value]);

  function handleFocus(e) {
    isFocused.current = true;
    e.target.select();
  }

  function handleKeyDown(e) {
    // Always allow control/meta combos (Ctrl+C, Ctrl+V, Ctrl+A, etc.)
    if (e.ctrlKey || e.metaKey) return;
    // Allow navigation and editing keys
    if (ALLOWED_KEYS.has(e.key)) return;
    // Allow digits, minus sign, comma, dot
    if (/^[\d\-,.]$/.test(e.key)) return;
    // Block everything else (letters, symbols, etc.)
    e.preventDefault();
  }

  function handleChange(e) {
    // Sanitize pasted content: keep only digits, minus, comma, dot
    const raw = e.target.value.replace(/[^\d\-,.]/g, '');
    setDisplay(raw);
    onChange({ target: { name, value: String(parseBRL(raw)) } });
  }

  function handleBlur() {
    isFocused.current = false;
    const num = parseBRL(display);
    setDisplay(formatBRL(num));
    onChange({ target: { name, value: String(num) } });
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      className={`${className} text-end${parseBRL(display) < 0 ? ' text-danger fw-bold' : ''}`}
      value={display}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      required={required}
      disabled={disabled}
    />
  );
}

export default CurrencyInput;
